// ---------------------------------------------------------------------------
// AN-008 Phase 4 — Reputation Weighted Score CQRS Event Catalog
//                  (vocabulary + Postgres rollup tier)
// ---------------------------------------------------------------------------
// Story AN-008 (ANALYTICS-REPORTING-ATOMIC-STORY-REGISTRY, 2026-05-13):
//   "Reviews dashboard shows reputation score, platform breakdown, review
//    velocity, sentiment/theme trends, revenue impact."
//
// Per `docs/audits/analytics-reporting-reality-2026-05-17.md` AN-008 is the
// top-1 next-story target — Phase 3 (LANDED PRs #1780 / #1864) shipped the
// per-platform query + `GET /v1/analytics/tenants/:tenantId/reputation/
// by-platform` route + ROI sister-route under the AN-008 namespace, but
// the 80%-rating / 20%-response formula does NOT account for review-count
// volume or recency. Owner spec for this tranche locks a four-factor
// weighted formula:
//
//      reputation_score (0..100)
//        = 0.40 * rating_component       (avg star → 0..100)
//        + 0.30 * recency_component      (half-life-decayed window avg)
//        + 0.15 * volume_component       (log-scaled review count)
//        + 0.15 * response_rate_component (responded / received)
//
// This Phase 4 narrow follow-up catalogues the weighted-score lifecycle:
//
//   - `analytics.reputation.score_computed@v1` — the weighted-score
//     calculator produced a fresh per-(tenant, sourceType, window)
//     snapshot. Persisted to the `reputation_platform_score_rollups`
//     Postgres rollup table for sub-50ms dashboard reads, AND replayed
//     into the `reputation_score_weighted_mv` ClickHouse MV for
//     cross-tenant agency reporting.
//
// Distinct from `reputation.platform.score_recomputed@v1` (Phase 2,
// already shipped): that event fires when the ROI materialiser refreshes
// the LIVE 80/20 score read off `reputation_trend_mv`. The Phase 4 event
// fires when the WEIGHTED four-factor score is computed and persisted to
// the rollup table — two distinct compute paths, two distinct events, so
// downstream subscribers (dashboard cache invalidator, briefing engine,
// agency rollup, AN-018 anomaly detector baseline) can bind to whichever
// emit cadence matches their refresh requirement.
//
// Per CLAUDE.md Governance Rule #7 ("Event Catalog"): every event type
// MUST be catalogued before implementation. Phase 4 elevates the
// weighted-score computation to a canonical event type and locks the
// component-weight vocab so the formula cannot drift across the four
// emit sites (HTTP route, nightly recompute cron, review-ingest hook,
// response-recorded hook).
//
// What this file is NOT:
//  - It does not implement the score formula — that lives in
//    `packages/analytics/src/queries/reputation-score-query.ts` (Phase B).
//  - It does not declare the ClickHouse MV — that lives in
//    `packages/analytics/src/schema/reputation-mvs.ts` (additive).
//  - It does not declare HTTP routes — they land in
//    `workers/api-analytics/src/routes/reputation-score.ts` (Phase C).
//  - It does not change `reputation_trend_mv` or `queryReputationByPlatform`
//    (Phase 3 formula stays for the lighter-weight `by-platform` surface).
//
// What this file IS:
//  - Single source of truth for `analytics.reputation.score_computed@v1`.
//  - Closed `WeightedScoreComponent` vocab pinning the four factors so
//    DLQ + audit-runner + AN-018 anomaly detector + briefing engine
//    cannot drift on component names.
//  - Closed `weightingProfile` vocab (default | recency_biased |
//    volume_biased) so the future per-vertical weighting tranche cannot
//    re-invent profile strings.
//  - Postgres rollup table `reputation_platform_score_rollups` —
//    one row per `(tenantId, sourceType, window)`; powers sub-50ms
//    dashboard read by sidestepping ClickHouse round-trip on cache miss.
//  - Typed payload contract pinning component values, weights, and the
//    sample inputs (rating_sum / review_count / responded_count /
//    recency_decayed_rating_sum) so DLQ replay reconstructs the exact
//    input shape.
//  - Runtime narrowing guards rejecting unknown event types / component
//    names / profile names at the boundary.
//
// CLAUDE.md Governance touched:
//   #5 Authoritative source guard — rollup row is the authoritative read
//      path; ClickHouse MV is the cross-tenant projection.
//   #6 Atomic events — one fact per event (score computed) — no
//      compound recompute+publish event.
//   #7 Event catalog — every event type catalogued before implementation.
// ---------------------------------------------------------------------------

import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  real,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

/* -------------------------------------------------------------------------- */
/* Event type catalog                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Closed catalog of AN-008 Phase 4 weighted-score CQRS event types.
 * Versioned with `@v1` suffix per platform convention.
 *
 *   `analytics.reputation.score_computed@v1`
 *
 * One business fact per event (Governance Rule #6): the weighted-score
 * calculator produced a fresh per-(tenant, sourceType, window) snapshot.
 *
 * Distinct from Phase 2's `reputation.platform.score_recomputed@v1`:
 *  - Phase 2 event:   LIVE 80/20 ROI score off `reputation_trend_mv`
 *                     (consumed by ROI dashboard cache invalidator).
 *  - Phase 4 event:   WEIGHTED four-factor score off the new query
 *                     primitive + persisted to the Postgres rollup
 *                     (consumed by briefing engine, agency rollup,
 *                     AN-018 anomaly baseline).
 */
export const AN_008_REPUTATION_SCORE_EVENT_TYPES = [
  "analytics.reputation.score_computed@v1",
] as const;

export type An008ReputationScoreEventType =
  (typeof AN_008_REPUTATION_SCORE_EVENT_TYPES)[number];

/* -------------------------------------------------------------------------- */
/* Weighted-score component vocab                                             */
/* -------------------------------------------------------------------------- */

/**
 * Closed catalog of the four weighted-score component names. Pinned so
 * the formula cannot drift across emit sites — the payload carries one
 * `componentValues` map keyed by these strings and the
 * `reputation_platform_score_rollups` rollup denormalises each into a
 * `real` column.
 *
 *  - `rating`        — average star rating mapped to 0..100
 *                      (`sum(rating_sum) / sum(review_count) / 5 * 100`).
 *  - `recency`       — half-life-decayed window-average rating mapped to
 *                      0..100. Bias toward fresh reviews; older reviews
 *                      contribute less. Decay parameter pinned to the
 *                      query primitive.
 *  - `volume`        — log-scaled review-count component mapped to
 *                      0..100. `log1p(review_count) / log1p(SATURATION)`
 *                      where `SATURATION` = 100 reviews (locked by the
 *                      query primitive).
 *  - `responseRate`  — `sum(responded_count) / sum(review_count)` mapped
 *                      to 0..100. Operator-engagement signal.
 */
export const AN_008_WEIGHTED_SCORE_COMPONENT_VOCAB = [
  "rating",
  "recency",
  "volume",
  "responseRate",
] as const;

export type An008WeightedScoreComponent =
  (typeof AN_008_WEIGHTED_SCORE_COMPONENT_VOCAB)[number];

/**
 * Closed catalog of weighting profiles. The default profile locks the
 * four weights pinned in the file header; future per-vertical profiles
 * MUST register here before they can land in `customer_events.payload`.
 *
 *  - `default`         — 0.40 rating / 0.30 recency / 0.15 volume /
 *                        0.15 responseRate. Pinned weights for v1.
 *  - `recency_biased`  — reserved for hospitality / time-sensitive
 *                        verticals where stale reviews are misleading.
 *                        NOT YET wired; reserved string only.
 *  - `volume_biased`   — reserved for verticals where review-count is
 *                        the trust signal (e.g. marketplaces).
 *                        NOT YET wired; reserved string only.
 */
export const AN_008_WEIGHTING_PROFILE_VOCAB = [
  "default",
  "recency_biased",
  "volume_biased",
] as const;

export type An008WeightingProfile =
  (typeof AN_008_WEIGHTING_PROFILE_VOCAB)[number];

/**
 * Pinned weights for the `default` profile. Mirrored by the query
 * primitive and the rollup-table denormalised weight columns so a
 * formula drift trips type-check.
 */
export const AN_008_DEFAULT_WEIGHTS: Record<
  An008WeightedScoreComponent,
  number
> = {
  rating: 0.4,
  recency: 0.3,
  volume: 0.15,
  responseRate: 0.15,
} as const;

/**
 * Closed catalog of recompute triggers. Pinpoints WHY the weighted score
 * recomputed so the rollup cache invalidator + briefing engine can route
 * correctly.
 *
 *  - `http_request`        — operator hit the `/v1/analytics/reputation/score`
 *                            route; rollup read-through fanned out as a
 *                            cache-miss recompute.
 *  - `scheduled_refresh`   — nightly cron-driven recompute (hot tenant
 *                            cache warm path).
 *  - `review_ingested`     — `reputation.review.ingested@v1` fired; the
 *                            incremental materialiser recomputed the
 *                            affected `(tenant, sourceType, window)` row.
 *  - `response_recorded`   — `reputation.response.recorded@v1` fired;
 *                            responseRate component recomputed.
 */
export const AN_008_SCORE_RECOMPUTE_TRIGGER_VOCAB = [
  "http_request",
  "scheduled_refresh",
  "review_ingested",
  "response_recorded",
] as const;

export type An008ScoreRecomputeTrigger =
  (typeof AN_008_SCORE_RECOMPUTE_TRIGGER_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Window vocab                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Closed catalog of supported recompute windows. Mirrors the Phase 3
 * by-platform route window enum so the front-end can switch surfaces
 * (`by-platform` ↔ `score`) without inventing a new window vocabulary.
 *
 *  - `30d`   — last 30 days inclusive (default for dashboard tile)
 *  - `90d`   — last 90 days inclusive (quarter-on-quarter trend)
 *  - `365d`  — last 365 days inclusive (year-on-year benchmark)
 */
export const AN_008_SCORE_WINDOW_VOCAB = ["30d", "90d", "365d"] as const;

export type An008ScoreWindow = (typeof AN_008_SCORE_WINDOW_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Payload contract                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Payload carried by `analytics.reputation.score_computed@v1`. Mirrors
 * the row written to `reputation_platform_score_rollups` one-to-one so
 * DLQ replay reconstructs the rollup state exactly.
 */
export interface An008ReputationScoreComputedPayload {
  /** Tenant scope. UUID. */
  tenantId: string;
  /** Provider source key — matches `reputation_trend_mv.source_type`. */
  sourceType: string;
  /** Closed window vocab (`30d` / `90d` / `365d`). */
  window: An008ScoreWindow;
  /** ISO date (YYYY-MM-DD). Window start, inclusive. */
  windowStart: string;
  /** ISO date (YYYY-MM-DD). Window end, inclusive. */
  windowEnd: string;
  /** Final 0..100 integer score (weighted sum of components). */
  reputationScore: number;
  /** Closed weighting profile name. */
  weightingProfile: An008WeightingProfile;
  /** Why the recompute fired. */
  trigger: An008ScoreRecomputeTrigger;
  /** Component values 0..100 (pre-weight). */
  componentValues: Record<An008WeightedScoreComponent, number>;
  /** Weights used for this computation (mirrors `AN_008_DEFAULT_WEIGHTS`). */
  componentWeights: Record<An008WeightedScoreComponent, number>;
  /** Sample inputs from the underlying ClickHouse aggregate. */
  sampleInputs: {
    reviewCount: number;
    ratingSum: number;
    respondedCount: number;
    positiveCount: number;
    negativeCount: number;
    recencyDecayedRatingSum: number;
    recencyDecayedReviewCount: number;
  };
  /** ISO-8601 UTC timestamp the rollup row was last refreshed. */
  computedAt: string;
}

export interface An008ReputationScoreComputedEvent {
  type: "analytics.reputation.score_computed@v1";
  payload: An008ReputationScoreComputedPayload;
}

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

export function isAn008ReputationScoreEventType(
  value: unknown,
): value is An008ReputationScoreEventType {
  return (
    typeof value === "string" &&
    (AN_008_REPUTATION_SCORE_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isAn008WeightedScoreComponent(
  value: unknown,
): value is An008WeightedScoreComponent {
  return (
    typeof value === "string" &&
    (AN_008_WEIGHTED_SCORE_COMPONENT_VOCAB as readonly string[]).includes(value)
  );
}

export function isAn008WeightingProfile(
  value: unknown,
): value is An008WeightingProfile {
  return (
    typeof value === "string" &&
    (AN_008_WEIGHTING_PROFILE_VOCAB as readonly string[]).includes(value)
  );
}

export function isAn008ScoreRecomputeTrigger(
  value: unknown,
): value is An008ScoreRecomputeTrigger {
  return (
    typeof value === "string" &&
    (AN_008_SCORE_RECOMPUTE_TRIGGER_VOCAB as readonly string[]).includes(value)
  );
}

export function isAn008ScoreWindow(
  value: unknown,
): value is An008ScoreWindow {
  return (
    typeof value === "string" &&
    (AN_008_SCORE_WINDOW_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Postgres rollup table                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Per-(tenantId, sourceType, window) weighted-reputation-score rollup.
 *
 * Authoritative read path for the AN-008 dashboard tile + briefing
 * engine + agency rollup. Sub-50ms read by sidestepping the ClickHouse
 * round-trip; the row is refreshed on every
 * `analytics.reputation.score_computed@v1` emit (HTTP read-through or
 * cron). The same payload is mirrored into the `reputation_score_weighted_mv`
 * ClickHouse MV for cross-tenant agency reporting (out of this tranche
 * — the MV ships in Phase B alongside the query primitive).
 *
 * Idempotency: composite unique index on `(tenant_id, source_type, window)`
 * + `INSERT ... ON CONFLICT DO UPDATE` pattern in the materialiser.
 */
export const reputationPlatformScoreRollups = pgTable(
  "reputation_platform_score_rollups",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /** Provider source key. Mirrors `reputation_trend_mv.source_type`. */
    sourceType: text("source_type").notNull(),
    /** Closed window vocab token (`30d` / `90d` / `365d`). */
    window: text("window").notNull(),
    /** ISO date (YYYY-MM-DD). Inclusive. */
    windowStart: text("window_start").notNull(),
    /** ISO date (YYYY-MM-DD). Inclusive. */
    windowEnd: text("window_end").notNull(),
    /** Final 0..100 integer score. */
    reputationScore: integer("reputation_score").notNull(),
    /** Closed weighting profile name (`default` / `recency_biased` / ...). */
    weightingProfile: text("weighting_profile").notNull().default("default"),
    /** Component values 0..100 (pre-weight). Denormalised for index scans. */
    ratingComponent: real("rating_component").notNull(),
    recencyComponent: real("recency_component").notNull(),
    volumeComponent: real("volume_component").notNull(),
    responseRateComponent: real("response_rate_component").notNull(),
    /** Raw sample inputs from the ClickHouse aggregate. */
    reviewCount: integer("review_count").notNull(),
    respondedCount: integer("responded_count").notNull(),
    positiveCount: integer("positive_count").notNull(),
    negativeCount: integer("negative_count").notNull(),
    /** Sum of star ratings (numerator of rating component). */
    ratingSumScaled: integer("rating_sum_scaled").notNull(),
    /** Why the rollup refreshed (`http_request` / `scheduled_refresh` / ...). */
    trigger: text("trigger").notNull(),
    /** Full event payload (for DLQ replay / audit-runner). */
    payloadJson: jsonb("payload_json").notNull(),
    computedAt: timestamp("computed_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_reputation_platform_score_rollups_unique").on(
      table.tenantId,
      table.sourceType,
      table.window,
    ),
    index("idx_reputation_platform_score_rollups_tenant").on(table.tenantId),
    index("idx_reputation_platform_score_rollups_computed_at").on(
      table.computedAt,
    ),
  ],
);
